import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { DropdownModule } from 'primeng/dropdown';
import { EMPTY_FORM_PAGE_MODEL, FormModel, FormPageModel } from '../../global-data-models/form.model';
import { DEFAULT_FORM_PAGE_OPTIONS, OrderDir, FormPageOptions } from '../../global-services/form.service';
import { FormsViewFacade } from './forms-view.facade';
import { Subscription } from 'rxjs';
import { DatePipe, JsonPipe } from '@angular/common';
import { GlobalCopyToClipboardComponent } from '../../global-components/global-copy-to-clipboard/copy-to-clipboard.component';
import { FormControl } from '@angular/forms';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { CrudFormsComponent } from '../crud-forms/crud-forms.component';
import { CrudFormMode } from '../crud-forms/crud-forms.facade';
import { SurveyModule } from 'survey-angular-ui';
import { SurveyModel } from 'survey-core';
import { GlobalMessageService } from '../../global-services/global-message.service';

export interface TableConfig {
  first: number;
  rows: number;
  page: number;
  pageCount: number;
}

@Component({
  selector: 'app-forms-view',
  standalone: true,
  imports: [...COMMON_IMPORTS, TableModule, PaginatorModule, DropdownModule, DialogModule, CrudFormsComponent, SurveyModule],
  templateUrl: './forms-view.component.html',
  styleUrl: './forms-view.component.css',
})
export class FormsViewComponent implements OnInit, OnDestroy {
  public readonly CrudFormMode = CrudFormMode;
  public readonly OrderDir = OrderDir;

  @Input() orgId!: string;

  public formPage: FormPageModel = EMPTY_FORM_PAGE_MODEL;
  public pageOptions: FormPageOptions = DEFAULT_FORM_PAGE_OPTIONS;

  public showUpdateFormDialog: boolean = false;
  public updateFormId?: string;

  // Survey Preview Properties
  public showSurveyPreviewDialog: boolean = false;
  public surveyPreviewModel?: SurveyModel;
  public isMobilePreview: boolean = false;

  private uiStateSub?: Subscription;
  private searchSub?: Subscription;
  private statusFilterSub?: Subscription;
  private versionFilterSub?: Subscription;

  public readonly searchControl = new FormControl('');
  public readonly statusFilterControl = new FormControl('all');
  public readonly versionFilterControl = new FormControl('most-relevant');

  public readonly statusFilterOptions = [
    { label: 'Published & Draft', value: 'all' },
    { label: 'Published', value: 'published' },
    { label: 'Draft', value: 'draft' },
  ];

  public readonly versionFilterOptions = [
    { label: 'Most Relevant', value: 'most-relevant' },
    { label: 'All Versions', value: 'all-versions' },
  ];

  public constructor(
    private readonly facade: FormsViewFacade,
    private readonly messageService: GlobalMessageService,
  ) {
    this.setupSearchSubscription();
    this.setupFilterSubscriptions();
  }

  public async ngOnInit() {
    this.uiStateSub = this.facade.uiState$.subscribe((state) => {
      this.formPage = state.formPage;
    });

    this.updatePageOption('orgId', this.orgId);

    // Apply default filters
    this.applyStatusFilter(this.statusFilterControl.value || 'all');
    this.applyVersionFilter(this.versionFilterControl.value || 'most-relevant');

    await this.facade.loadForms();
  }

  private updatePageOption(key: string, value: any) {
    this.pageOptions = {
      ...this.pageOptions,
      [key]: value,
    };
    this.facade.setFormPageOptions(this.pageOptions);
  }

  private setupSearchSubscription() {
    this.searchSub = this.searchControl.valueChanges
      .pipe(
        debounceTime(300), // 300ms debounce
        distinctUntilChanged(),
      )
      .subscribe(async (searchTerm: string | null) => {
        this.updatePageOption('search', searchTerm || '');
        await this.facade.loadForms();
      });
  }

  private setupFilterSubscriptions() {
    // Status filter subscription
    this.statusFilterSub = this.statusFilterControl.valueChanges.pipe(distinctUntilChanged()).subscribe(async (statusFilter: string | null) => {
      this.applyStatusFilter(statusFilter || 'all');
      await this.facade.loadForms();
    });

    // Version filter subscription
    this.versionFilterSub = this.versionFilterControl.valueChanges.pipe(distinctUntilChanged()).subscribe(async (versionFilter: string | null) => {
      this.applyVersionFilter(versionFilter || 'most-relevant');
      await this.facade.loadForms();
    });
  }

  private applyStatusFilter(statusFilter: string) {
    if (statusFilter === 'all') {
      this.updatePageOption('status', undefined);
    } else if (statusFilter === 'published') {
      this.updatePageOption('status', 'PUBLISHED');
    } else if (statusFilter === 'draft') {
      this.updatePageOption('status', 'DRAFT');
    }
  }

  private applyVersionFilter(versionFilter: string) {
    if (versionFilter === 'most-relevant') {
      // Most relevant = all active versions
      this.updatePageOption('isActive', true);
    } else if (versionFilter === 'all-versions') {
      // All versions = both active and inactive
      this.updatePageOption('isActive', undefined);
    }
  }

  public async onPageEvent(event: TableLazyLoadEvent) {
    this.updatePageOption('limit', event.rows);
    this.updatePageOption('page', (event.first || 0) / (event.rows || 1) + 1);
    await this.facade.loadForms();
  }

  public async onSortEvent(event: any) {
    this.updatePageOption('orderDir', event.order === 1 ? OrderDir.ASC : OrderDir.DESC);
    this.updatePageOption('orderBy', event.field);
    await this.facade.loadForms();
  }

  public async onCardSort(field: string) {
    const newOrder = this.pageOptions.orderBy === field && this.pageOptions.orderDir === OrderDir.ASC ? OrderDir.DESC : OrderDir.ASC;

    this.updatePageOption('orderDir', newOrder);
    this.updatePageOption('orderBy', field);
    await this.facade.loadForms();
  }

  public async onPaginatorPageEvent(event: any) {
    this.updatePageOption('limit', event.rows);
    this.updatePageOption('page', (event.first || 0) / (event.rows || 1) + 1);
    await this.facade.loadForms();
  }

  public startUpdateForm(formId: string) {
    this.updateFormId = formId;
    this.showUpdateFormDialog = true;
  }

  public stopUpdateForm() {
    this.showUpdateFormDialog = false;
    this.updateFormId = undefined;
  }

  public async onUpdateFormSuccess() {
    await this.reloadForms();
  }

  public async reloadForms(): Promise<void> {
    this.showUpdateFormDialog = false;
    this.updateFormId = undefined;
    await this.facade.loadForms();
    // Apply default filters
    this.applyStatusFilter('all');
    this.applyVersionFilter('most-relevant');
  }

  // Methods for Survey Preview Dialog
  public openSurveyPreviewDialog(form: FormModel): void {
    const formDataString = form.formData;

    if (!formDataString || formDataString.trim() === '') {
      this.messageService.showError('Error', 'No form data to preview. This form has no content.');
      return;
    }

    try {
      const formData = typeof formDataString === 'string' ? JSON.parse(formDataString) : formDataString;
      this.surveyPreviewModel = new SurveyModel(formData);
      this.showSurveyPreviewDialog = true;
    } catch (error) {
      console.error('Error parsing form data for preview:', error);
      this.messageService.showError('Error', 'Could not parse form data for preview. Please check your form JSON.');
    }
  }

  public closeSurveyPreviewDialog(): void {
    this.showSurveyPreviewDialog = false;
    this.surveyPreviewModel = undefined; // Clear the model
    this.isMobilePreview = false; // Reset mobile mode
  }

  public toggleMobilePreview(): void {
    this.isMobilePreview = !this.isMobilePreview;
  }

  public ngOnDestroy(): void {
    this.uiStateSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.statusFilterSub?.unsubscribe();
    this.versionFilterSub?.unsubscribe();
    this.facade.destroy();
  }
}
